<template>
  <div class="print-page">
    <div
      class="no-print d-flex justify-content-between align-items-center mb-3"
    >
      <div>
        <h3 class="mb-0">Comprovante de assinatura</h3>
        <small class="text-muted">Aluguel #{{ rentNumber }}</small>
      </div>
      <div class="d-flex gap-1">
        <b-button variant="outline-secondary" @click="goBack">
          Voltar
        </b-button>
        <b-button variant="primary" @click="savePdf">Salvar PDF</b-button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-4">Carregando...</div>
    <b-alert v-else-if="error" show variant="danger">{{ error }}</b-alert>

    <div v-else class="document">
      <div class="document-header mb-3">
        <div>
          <div class="title">Resumo</div>
          <div class="muted">Gerado em: {{ new Date() | formatDateTime }}</div>
        </div>
        <div class="muted text-right">
          <div v-if="signatureSignedAt">
            Assinado em: {{ signatureSignedAt | formatDateTime }}
          </div>
        </div>
      </div>

      <b-card no-body class="mb-3">
        <b-card-header class="py-2">
          <strong>Geral</strong>
        </b-card-header>
        <b-card-body class="py-2">
          <b-row>
            <b-col cols="12" md="6" class="mb-2">
              <small class="text-muted d-block">Loja</small>
              <div>{{ storeName }}</div>
            </b-col>
            <b-col cols="12" md="6" class="mb-2">
              <small class="text-muted d-block">Inicio do aluguel</small>
              <div>
                <span v-if="rentCreatedAt">
                  {{ rentCreatedAt | formatDateTime }}
                </span>
                <span v-else>-</span>
              </div>
            </b-col>
            <b-col v-if="expectedReturnAt" cols="12" md="6" class="mb-2">
              <small class="text-muted d-block">Retorno previsto</small>
              <div>{{ expectedReturnAt | formatDateTime }}</div>
            </b-col>
            <b-col v-if="finishedAt" cols="12" md="6" class="mb-2">
              <small class="text-muted d-block">Retorno real</small>
              <div>{{ finishedAt | formatDateTime }}</div>
            </b-col>
            <b-col
              v-if="rent && rent.pausedAt && !finishedAt"
              cols="12"
              md="6"
              class="mb-2"
            >
              <small class="text-muted d-block">Pausado em</small>
              <div>{{ rent.pausedAt | formatDateTime }}</div>
            </b-col>

            <b-col cols="12" md="6" class="mb-2">
              <small class="text-muted d-block">Brinquedo</small>
              <div>{{ toyName }}</div>
            </b-col>
            <b-col cols="12" md="6" class="mb-2">
              <small class="text-muted d-block">Periodo</small>
              <div>{{ periodText }}</div>
            </b-col>

            <b-col cols="12" md="6" class="mb-2">
              <small class="text-muted d-block">Cliente</small>
              <div>{{ customerName }}</div>
            </b-col>
            <b-col cols="12" md="6" class="mb-2">
              <small class="text-muted d-block">CPF</small>
              <div>{{ customerDocument }}</div>
            </b-col>

            <b-col cols="12" md="6" class="mb-2">
              <small class="text-muted d-block">Telefone</small>
              <div>{{ customerPhone }}</div>
            </b-col>
            <b-col
              v-if="customerEmail && customerEmail !== '-'"
              cols="12"
              md="6"
              class="mb-2"
            >
              <small class="text-muted d-block">Email</small>
              <div>{{ customerEmail }}</div>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>

      <b-row class="mb-3">
        <b-col cols="12" lg="6" class="mb-3 mb-lg-0">
          <b-card no-body class="h-100">
            <b-card-header class="py-2">
              <strong>Tempos</strong>
            </b-card-header>
            <b-card-body class="py-2">
              <b-row>
                <b-col cols="12" md="6" class="mb-2">
                  <small class="text-muted d-block">Permanencia total</small>
                  <div>{{ formatMinutes(totalMinutes) }}</div>
                </b-col>
                <b-col cols="12" md="6" class="mb-2">
                  <small class="text-muted d-block">Tempo efetivo</small>
                  <div>{{ formatMinutes(netMinutes) }}</div>
                </b-col>

                <b-col v-if="pausedMinutes > 0" cols="12" md="6" class="mb-2">
                  <small class="text-muted d-block">Tempo pausado</small>
                  <div>{{ formatMinutes(pausedMinutes) }}</div>
                </b-col>
                <b-col v-if="extraMinutes > 0" cols="12" md="6" class="mb-2">
                  <small class="text-muted d-block">Tempo adicional</small>
                  <div>{{ formatMinutes(extraMinutes) }}</div>
                </b-col>
                <b-col>
                  <div v-if="extraTimeReasonsText" class="mt-2">
                    <small class="text-muted d-block"
                      >Motivo(s) tempo adicional</small
                    >
                    <div>{{ extraTimeReasonsText }}</div>
                  </div>
                </b-col>
                <b-col cols="12" md="6" class="mb-2">
                  <small class="text-muted d-block">Tempo cobrado</small>
                  <div>{{ formatMinutes(timeChargedMinutes) }}</div>
                </b-col>
                <b-col v-if="exceededMinutes > 0" cols="12" md="6" class="mb-2">
                  <small class="text-muted d-block">Excedido</small>
                  <div>{{ formatMinutes(exceededMinutes) }}</div>
                </b-col>
              </b-row>
            </b-card-body>
          </b-card>
        </b-col>

        <b-col cols="12" lg="6">
          <b-card no-body class="h-100">
            <b-card-header
              class="py-2 d-flex justify-content-between align-items-center"
            >
              <strong>Pagamentos</strong>
              <b-badge
                :variant="hasPayments ? 'success' : 'secondary'"
                class="payments-status-badge"
              >
                {{ hasPayments ? 'Registrado' : 'Nao registrado' }}
              </b-badge>
            </b-card-header>
            <b-card-body class="py-2">
              <div v-if="hasPayments">
                <div
                  v-for="line in paymentLines"
                  :key="line.type"
                  class="d-flex justify-content-between"
                >
                  <span>{{ line.label }}</span>
                  <strong>{{ line.amount | formatCurrency }}</strong>
                </div>
                <div
                  class="d-flex justify-content-between border-top pt-2 mt-2"
                >
                  <span><strong>Total</strong></span>
                  <span
                    ><strong>{{ paymentsTotal | formatCurrency }}</strong></span
                  >
                </div>
              </div>
              <div v-else class="text-muted">
                Nenhum pagamento registrado para este aluguel.
              </div>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12" lg="4" class="mb-3 mb-lg-0">
          <b-card no-body>
            <b-card-header class="py-2">
              <strong>Assinatura</strong>
            </b-card-header>
            <b-card-body class="bg-white">
              <img
                v-if="signatureSvgDataUrl"
                :src="signatureSvgDataUrl"
                alt="Assinatura"
                class="signature-image"
              />
              <div v-else class="text-muted">Assinatura indisponivel.</div>
            </b-card-body>
          </b-card>
        </b-col>

        <b-col cols="12" lg="8" class="mb-3 mb-lg-0">
          <b-card no-body class="term-card">
            <b-card-header class="py-2">
              <strong>Termo assinado</strong>
            </b-card-header>
            <b-card-body>
              <div class="terms">{{ termAcceptText }}</div>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import services from '@/services'
import { formatDateTime } from '@/helpers/date'
import { formatCurrency } from '@/helpers/numbers'
import { calculateExtraTime, calculatePausedTime } from '@/helpers/rents'
import { differenceInMinutes } from 'date-fns'

export default {
  name: 'RentSignaturePrint',
  filters: { formatDateTime, formatCurrency },
  data() {
    return {
      loading: true,
      error: null,
      rent: null,
      signature: null
    }
  },
  computed: {
    rentId() {
      return Number(this.$route.params.id)
    },
    returnTo() {
      const value = this.$route.query && this.$route.query.returnTo
      return typeof value === 'string' && value.startsWith('/') ? value : null
    },
    rentNumber() {
      return (this.rent && this.rent.id) || this.rentId || '—'
    },
    storeName() {
      return (this.rent && this.rent.store && this.rent.store.name) || '-'
    },
    rentCreatedAt() {
      return (this.rent && this.rent.createdAt) || null
    },
    toyName() {
      return (this.rent && this.rent.toy && this.rent.toy.name) || '-'
    },
    periodText() {
      const minutes = this.rent && this.rent.period && this.rent.period.time
      return minutes ? `${minutes} min` : '-'
    },
    expectedReturnAt() {
      const endDate = this.rent && this.rent.endDate
      return endDate ? new Date(endDate) : null
    },
    finishedAt() {
      if (!this.rent) return null
      return this.rent.endedAt || this.rent.canceledAt || null
    },
    statusLabel() {
      if (!this.rent) return '-'
      if (this.rent.canceledAt) return 'Cancelado'
      if (this.rent.endedAt) return 'Encerrado'
      if (this.rent.pausedAt) return 'Pausado'
      if (this.rent.status === 'rescued') return 'Resgatado'
      return 'Ativo'
    },
    statusVariant() {
      if (!this.rent) return 'secondary'
      if (this.rent.canceledAt) return 'danger'
      if (this.rent.endedAt) return 'secondary'
      if (this.rent.pausedAt) return 'warning'
      return 'success'
    },
    customerName() {
      return (this.rent && this.rent.customer && this.rent.customer.name) || '-'
    },
    customerDocument() {
      return (
        (this.rent && this.rent.customer && this.rent.customer.document) || '-'
      )
    },
    customerPhone() {
      return (
        (this.rent && this.rent.customer && this.rent.customer.phone) || '-'
      )
    },
    customerEmail() {
      return (
        (this.rent && this.rent.customer && this.rent.customer.email) || '-'
      )
    },
    signatureSignedAt() {
      return (this.signature && this.signature.signedAt) || null
    },
    termAcceptText() {
      return (
        (this.signature && this.signature.termAccept) || 'Termo indisponivel.'
      )
    },
    signatureSvgDataUrl() {
      const svg = (this.signature && this.signature.signatureSvg) || ''
      if (!svg) return ''
      return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
    },
    totalMinutes() {
      if (!this.rentCreatedAt) return 0
      const startDate = new Date(this.rentCreatedAt)
      const endDate = this.finishedAt ? new Date(this.finishedAt) : new Date()
      return Math.max(0, differenceInMinutes(endDate, startDate))
    },
    pausedMinutes() {
      if (!this.rent) return 0
      const basePaused = calculatePausedTime(this.rent) || 0
      const pausedAt = this.rent.pausedAt
      if (!pausedAt || this.finishedAt) return basePaused
      return (
        basePaused +
        Math.max(0, differenceInMinutes(new Date(), new Date(pausedAt)))
      )
    },
    extraMinutes() {
      if (!this.rent) return 0
      return calculateExtraTime(this.rent) || 0
    },
    netMinutes() {
      return Math.max(0, this.totalMinutes - this.pausedMinutes)
    },
    timeChargedMinutes() {
      return Math.max(0, this.netMinutes - this.extraMinutes)
    },
    exceededMinutes() {
      const periodMinutes =
        this.rent && this.rent.period && this.rent.period.time
      if (!periodMinutes) return 0
      return Math.max(0, this.timeChargedMinutes - periodMinutes)
    },
    extraTimeReasonsText() {
      const list = Array.isArray(this.rent?.extraTime)
        ? this.rent.extraTime
        : []
      const reasons = list
        .filter((it) => it && it.type !== 'pause')
        .map((it) => it.reason)
        .filter((reason) => typeof reason === 'string' && reason.trim().length)
      if (!reasons.length) return ''
      return [...new Set(reasons)].join(' / ')
    },
    paymentLines() {
      const list = Array.isArray(this.rent && this.rent.payments)
        ? this.rent.payments
        : Array.isArray(this.rent && this.rent.receivables)
        ? this.rent.receivables
        : []

      const totalsByType = {}
      list.forEach((payment) => {
        if (!payment) return
        const type = payment.type || 'other'
        totalsByType[type] =
          (totalsByType[type] || 0) + Number(payment.amount || 0)
      })

      const labelsByType = {
        cash: 'Dinheiro',
        pix: 'PIX',
        debitCard: 'Cartao de debito',
        creditCard: 'Cartao de credito'
      }

      const order = ['cash', 'pix', 'debitCard', 'creditCard']
      const known = order
        .filter((type) => totalsByType[type] > 0)
        .map((type) => ({
          type,
          label: labelsByType[type],
          amount: totalsByType[type]
        }))

      const unknown = Object.keys(totalsByType)
        .filter((type) => !order.includes(type) && totalsByType[type] > 0)
        .sort((a, b) => a.localeCompare(b))
        .map((type) => ({
          type,
          label: labelsByType[type] || type,
          amount: totalsByType[type]
        }))

      return [...known, ...unknown]
    },
    paymentsTotal() {
      return this.paymentLines.reduce((acc, it) => acc + it.amount, 0)
    },
    hasPayments() {
      return this.paymentsTotal > 0
    }
  },
  async created() {
    if (!this.rentId) {
      this.error = 'Aluguel invalido.'
      this.loading = false
      return
    }

    this.loading = true
    this.error = null
    try {
      const [rent, signature] = await Promise.all([
        services.rents.fetchRentById(this.rentId),
        services.rents.fetchRentSignature(this.rentId)
      ])
      this.rent = rent
      this.signature = signature
    } catch (e) {
      this.error =
        e?.response?.data?.message ||
        e?.message ||
        'Nao foi possivel carregar o comprovante.'
    } finally {
      this.loading = false
    }
  },
  methods: {
    goBack() {
      if (this.returnTo) {
        this.$router.push(this.returnTo)
        return
      }
      this.$router.back()
    },
    formatMinutes(minutes) {
      const total = Math.max(0, Number(minutes || 0))
      const hours = Math.floor(total / 60)
      const remainingMinutes = total % 60
      if (!hours) return `${total} min`
      return `${total} min (${hours}h${String(remainingMinutes).padStart(
        2,
        '0'
      )})`
    },
    savePdf() {
      window.print()
    }
  }
}
</script>

<style scoped>
.print-page {
  padding: 16px;
}

.document-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: baseline;
}

.title {
  font-weight: 600;
}

.muted {
  color: #6c757d;
}

.signature-image {
  max-width: 100%;
  max-height: 320px;
}

.terms {
  white-space: pre-wrap;
  font-family: inherit;
  margin: 0;
}

@media print {
  .no-print {
    display: none !important;
  }
  .print-page {
    padding: 0;
  }
  .document {
    font-size: 20px;
    zoom: 0.92;
  }
  .mb-3 {
    margin-bottom: 0.35rem !important;
  }
  .mb-2 {
    margin-bottom: 0.2rem !important;
  }
  .card {
    break-inside: avoid-page;
    page-break-inside: avoid;
  }
  .card-body {
    padding: 0.45rem !important;
  }
  .card-header {
    padding: 0.35rem 0.45rem !important;
  }
  .payments-status-badge {
    border: none !important;
    box-shadow: none !important;
    outline: none !important;
  }
  .signature-image {
    max-height: 180px;
  }
  .terms {
    font-size: 9px;
    line-height: 1.2;
    columns: 1;
    column-gap: 12px;
    max-height: 300px;
    overflow: hidden;
  }
  @page {
    size: A4;
    margin: 8mm;
  }
}
</style>
